import { API_BASE_URL } from '@wundo/client/api.ts';
import {
  DICTATION_PATH,
  type DictationClientEvent,
  type DictationServerEvent,
} from '@wundo/shared/dictation.ts';
import { createPcmCapture, PCM_SAMPLE_RATE } from './createPcmCapture.ts';

/* The server answers within a second or two of a stop; past this it is not coming. */
const ANSWER_TIMEOUT_MS = 15_000;

/**
 * One dictation over the API's dictation socket: the microphone goes up as PCM
 * while the user talks, the same way a spoken practice sends it, and the words
 * come back once it is stopped.
 *
 * The socket opens as soon as this is made, alongside the microphone prompt;
 * audio and the stop that arrive before it is open wait for it in order.
 */
export class DictationStream {
  /** What was said, trimmed; empty when nothing was. Rejects when it cannot be heard. */
  public readonly text: Promise<string>;

  private readonly socket: WebSocket;
  private readonly unsent: Array<ArrayBuffer | string> = [];
  private stream: MediaStream | null = null;
  private context: AudioContext | null = null;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private stopped = false;
  private settled = false;
  private resolveText: (text: string) => void = () => undefined;
  private rejectText: (error: Error) => void = () => undefined;

  constructor() {
    this.text = new Promise<string>((resolve, reject) => {
      this.resolveText = resolve;
      this.rejectText = reject;
    });

    const socket = new WebSocket(
      `${API_BASE_URL.replace(/^http/, 'ws')}${DICTATION_PATH}`,
    );
    socket.binaryType = 'arraybuffer';
    this.socket = socket;

    socket.onopen = () => {
      this.unsent.splice(0).forEach((frame) => socket.send(frame));
    };
    socket.onmessage = (event: MessageEvent<unknown>) => this.receive(event.data);
    socket.onclose = (event) => {
      this.fail(new Error(`Dictation socket closed (${event.code})`));
    };
  }

  /** Send what `stream` hears. The stream is this dictation's to stop from now on. */
  async listen(stream: MediaStream): Promise<void> {
    this.stream = stream;
    if (this.settled) {
      this.release();
      return;
    }

    const context = new AudioContext({ sampleRate: PCM_SAMPLE_RATE });
    this.context = context;

    const capture = await createPcmCapture(context, (message) =>
      this.send(message.pcm.buffer),
    );
    /* Stopped or thrown away while the worklet loaded. */
    if (this.context !== context) {
      return;
    }

    context.createMediaStreamSource(stream).connect(capture);
    await context.resume();
  }

  /** The user is done: let go of the microphone and wait for the words. */
  stop(): void {
    if (this.stopped || this.settled) {
      return;
    }

    this.stopped = true;
    this.release();
    this.send(JSON.stringify({ type: 'stop' } satisfies DictationClientEvent));
    this.timer = setTimeout(
      () => this.fail(new Error('Dictation was not answered')),
      ANSWER_TIMEOUT_MS,
    );
  }

  /** Throw it away: nothing is read and `text` never settles. */
  cancel(): void {
    this.settled = true;
    this.finish();
  }

  private receive(data: unknown): void {
    if (typeof data !== 'string') {
      return;
    }

    let event: DictationServerEvent;
    try {
      event = JSON.parse(data) as DictationServerEvent;
    } catch {
      return;
    }

    if (event.type === 'text') {
      this.succeed(event.text.trim());
      return;
    }

    this.fail(new Error(event.message));
  }

  private send(frame: ArrayBuffer | string): void {
    if (this.socket.readyState === WebSocket.CONNECTING) {
      this.unsent.push(frame);
      return;
    }

    if (this.socket.readyState === WebSocket.OPEN) {
      this.socket.send(frame);
    }
  }

  private succeed(text: string): void {
    if (this.settled) {
      return;
    }

    this.settled = true;
    this.finish();
    this.resolveText(text);
  }

  private fail(error: Error): void {
    if (this.settled) {
      return;
    }

    this.settled = true;
    this.finish();
    this.rejectText(error);
  }

  private finish(): void {
    if (this.timer !== null) {
      clearTimeout(this.timer);
      this.timer = null;
    }

    this.release();
    this.unsent.length = 0;
    this.socket.close(1_000);
  }

  private release(): void {
    this.stream?.getTracks().forEach((track) => track.stop());
    this.stream = null;

    const context = this.context;
    this.context = null;
    void context?.close().catch(() => undefined);
  }
}
